//Built-in libraries

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
//Third party libraries
import { useParams } from 'react-router';
import ProjectSocialGoalsHookFieldArray, {
  projectSocialGoalsValidationSchema,
} from '@/components/Sherpa/Projects/ProjectSocialGoalsHookFieldArray';
//Custom components
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProject } from '../../../../../../../api/Sherpa/Project';
import type { IPatchProjectServicePayload } from '../../../../../../../api/Sherpa/Project/modules/ProjectCore/types';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
//Services
import { notifyUser } from '../../../../../../../store/Toast';
//Assets andd Miscellaneous
import type { GoalInterface } from '../../../../../../../types/Project';
import { sanitizeResponseError } from '../../../../../../../utils';
import goalTotalPercentage from '../../../../../../../utils/project';

//Component Begin
interface IChangeProjectSocialGoalsProps {
  handleModal: () => void;
}

const ChangeProjectSocialGoals = ({ handleModal }: IChangeProjectSocialGoalsProps) => {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchProject = useProject({ projectId: Number(projectId) });
  const projectSocialGoals = fetchProject?.data?.data?.goals;

  const patchProjectGoals = useMutation(
    (data: Partial<IPatchProjectServicePayload>) =>
      patchProject({
        projectId: Number(projectId),
        data,
      }),
    {
      onSuccess: () => {
        handleModal();
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Social Goals saved successfully.',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (data: { goals: GoalInterface[] }) => {
    const totalProjectSocialGoals = goalTotalPercentage(data.goals);
    if (totalProjectSocialGoals > 100) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Total Project Social Goals percentage cannot be greater than 100%',
          toastType: 'failed',
        })
      );
    } else {
      patchProjectGoals.mutate(data);
    }
  };
  return (
    <Modal>
      <HookFormProvider
        defaultValues={{ goals: projectSocialGoals }}
        resolver={yupResolver(projectSocialGoalsValidationSchema) as any}
        mode='all'
      >
        <HookForm onSubmit={onSubmit} className='flex flex-col max-h-96'>
          <h2 className='mt-0'>Update Project Social Goals</h2>
          <ProjectSocialGoalsHookFieldArray />
          <div className='flex justify-end  gap-4 mt-5'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={handleModal} />
            <Button
              label={`${patchProjectGoals.isLoading ? 'Saving...' : 'Save'}`}
              btnStyle='btn-primary'
              disabled={patchProjectGoals.isLoading}
              type='submit'
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
};

export default ChangeProjectSocialGoals;
